/**
 * Маршруты живой встречи — одно место на всё приложение.
 *
 * Зачем. 19.09.2026 выкатка перезагрузила комнату посреди интервью: новый
 * service worker вставал сразу и забирал вкладку. Первую дверь закрыли
 * (баннер «Доступно обновление» вместо мгновенного обновления), но осталась
 * вторая, поуже: worker регистрировался на КАЖДОЙ странице, включая комнату,
 * и держал в ней слушателя `controlling` → `location.reload()`. Человек в
 * соседней вкладке нажимал «Обновить», новый worker забирал все клиенты —
 * и живая встреча перезагружалась. Плюс телефон кандидата в первые секунды
 * встречи качал в кэш всю CRM (около 6 МБ), отнимая канал у видео.
 *
 * Поэтому на этих маршрутах service worker не регистрируется вовсе: ни
 * обновления, ни предзагрузки, ни слушателей. Обновление дождётся выхода.
 *
 * Списка два, и это важно (аудит 20.09.2026). Сначала в оба попал и
 * `/video-interviews` — список встреч. Но это обычный рабочий экран CRM, а не
 * живая встреча: рекрутёр держит его открытым весь день. Worker
 * регистрируется один раз при загрузке страницы, поэтому у того, кто зашёл в
 * CRM со списка, он не вставал бы никогда — ни кэша, ни баннера «Доступно
 * обновление» во всей вкладке.
 *
 * Оба списка повторены в фильтре HTML-кэша (shared/pwaNavigation.ts) — там
 * они запрещают отдавать эти адреса из кэша. Расхождение ловит тест
 * `videoRoomPath.test.ts`, он читает vite.config.ts и сверяет.
 */

/**
 * Начала адресов ЖИВОЙ встречи и просмотра записи. Здесь service worker не
 * регистрируется вовсе: ни обновления, ни предзагрузки, ни слушателей.
 *
 * • `/video-interview/` — комната рекрутёра и старые ссылки кандидата;
 * • `/i/` — короткая ссылка кандидата;
 * • `/interview-view/` — просмотр записи заказчиком.
 */
export const VIDEO_LIVE_PATH_PREFIXES = [
  "/video-interview/",
  "/i/",
  "/interview-view/",
] as const;

/**
 * Адреса, которые не отдаются из кэша навигации: живая встреча плюс список
 * встреч. Список можно открыть из кэша и увидеть вчерашние данные, поэтому он
 * тоже всегда берётся с сервера — но worker на нём работает как везде.
 */
export const VIDEO_ROOM_PATH_PREFIXES = [
  ...VIDEO_LIVE_PATH_PREFIXES,
  "/video-interviews",
] as const;

/** Идёт ли по этому адресу ЖИВАЯ встреча или просмотр записи. */
export function isVideoLivePath(pathname: string): boolean {
  return VIDEO_LIVE_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

/** Нужно ли брать этот адрес только с сервера, минуя кэш навигации. */
export function isVideoRoomPath(pathname: string): boolean {
  return VIDEO_ROOM_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

/**
 * Служебная комната рекрутёра `/video-interview/<номер>/room`. Кандидату этот
 * адрес не подходит: без входа сотрудника он открывает экран входа CRM, и там
 * нужна подсказка, где взять настоящую ссылку-приглашение (02.11.2026).
 */
export function isRecruiterRoomPath(pathname: string): boolean {
  return /^\/video-interview\/\d+\/room\/?$/.test(pathname);
}
